import { MouseEvent } from "react";
import { useLocation } from "react-router-dom";

import useCreateEmptyAssessment from "@apps/assessments/hooks/use-create-empty-assessment";
import useLabel from "@apps/use-label/use-label";
import AppLink from "@components/link/link";

/**
 * Where a calibration waiting for a manager review is unblocked: the reader's own manager
 * review of that person.
 *
 * A draft the reader has started opens as it is. With no draft yet, a click creates one
 * and opens it, as "Start" does on the reader's review tasks. The link still points at
 * those tasks, so a middle click or a new tab lands somewhere useful.
 */
const GoToManagerReviewLink = ({
  complianceProgramId,
  assessmentTemplateId,
  targetId,
  assessmentId,
}: {
  complianceProgramId: number;
  assessmentTemplateId: number;
  targetId: number;
  assessmentId?: number | null;
}) => {
  const label = useLabel();
  const location = useLocation();
  // The search too, so the reader comes back to the Progress tab as they left it.
  const previousPathname = `${location.pathname}${location.search}`;
  const { handleCreateEmptyAssessment, isLoading } =
    useCreateEmptyAssessment(previousPathname);

  if (assessmentId) {
    return (
      <AppLink
        to={{
          pathname: `/assessments/assessment/${assessmentId}`,
          state: { previousPathname },
        }}
        className="text-sm text-blue-600 hover:underline"
      >
        Go to your {label("review")}
      </AppLink>
    );
  }

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    if (!isLoading) {
      handleCreateEmptyAssessment(
        complianceProgramId,
        assessmentTemplateId,
        targetId,
      );
    }
  };

  return (
    <AppLink
      to="/assessments"
      onClick={handleClick}
      className="text-sm text-blue-600 hover:underline"
    >
      {isLoading ? "Creating..." : `Start your ${label("review")}`}
    </AppLink>
  );
};

export default GoToManagerReviewLink;
